import React, { Ref } from "react";

type Option = {
  id?: string;
  label?: string;
  name?: string;
  value?: string;
};

export type DataAttributeForTesting = string;

const Input = React.forwardRef(
  (
    {
      defaultValue = "",
      disabled = false,
      errors,
      label,
      name,
      placeholder = label,
      register = () => null,
      rules, // ---- https://react-hook-form.com/get-started#Applyvalidation
      type = "text",
      autoComplete,
      onBlur = () => {},
      options,
      testId,
    }: {
      defaultValue?: string;
      disabled?: boolean;
      errors: any;
      label: string;
      name: string;
      options?: Option[];
      placeholder?: string;
      register: any;
      rules?: any;
      type?: string;
      autoComplete?: "email";
      onBlur?: Function;
      testId?: DataAttributeForTesting;
    },
    ref?: Ref<HTMLDivElement>
  ) => {
    const errorStyle = "border border-2 border-red-400 -mb-0.5";

    let error = errors?.[name]?.message;
    // recurse down the error object to find the error message if the name is a path
    if (!error && name.includes(".")) {
      let subPaths = name.split(".");
      let currentErrorLevel = errors;
      for (const subPath of subPaths) {
        currentErrorLevel = currentErrorLevel[subPath] || {};
      }
      error = currentErrorLevel?.message;
    }

    if (type === "select") {
      return (
        <div className="flex flex-col mt-2">
          {label && <label className="mt-2 md:basis-1/4 text-s">{label}</label>}
          <select
            {...register(name, { ...rules, onBlur })}
            className={`bg-white appearance-none border border-gray-200
                rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none
                focus:bg-white focus:border-blue-500`}
            defaultValue={defaultValue}
            id={`${label} input`}
            data-testid={testId}
          >
            <option value="" disabled>
              {placeholder ? placeholder : "Select an option"}
            </option>
            {options?.map((o, index) => {
              return (
                <option key={index} value={o.id || o.value}>
                  {o.name || o.label}
                </option>
              );
            })}
          </select>
          <span className="text-red-400 text-sm flex items-end pl-2">
            <span
              className={`transition-opacity duration-300 ${
                error ? "opacity-100" : "opacity-0"
              }`}
            >
              {error}
            </span>
          </span>{" "}
        </div>
      );
    }

    return (
      <div className="flex flex-col mt-2">
        {label && <label className="mt-2 md:basis-1/4 text-s">{label}</label>}
        <input
          ref={ref}
          id={`${label} input`}
          className={`p-2 border rounded shrink transition-all duration-200 ${
            error && errorStyle
          }`}
          placeholder={placeholder}
          defaultValue={defaultValue}
          type={type}
          disabled={disabled}
          {...register(name, { ...rules, onBlur })}
          autoComplete={autoComplete}
          data-testid={testId}
        />

        <span className="text-red-400 text-sm flex items-end pl-2">
          <span
            className={`transition-opacity duration-300 ${
              error ? "opacity-100" : "opacity-0"
            }`}
          >
            {error}
          </span>
        </span>
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
